import SaveIcon from '@mui/icons-material/Save'
import { Box, Fab, Grid, TextField, Tooltip, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { EnqueueSnackbarType, showMessage } from '../../common/Helpers'

interface IProps {
    config: { name: string, value: string, description: string }

    editConfig(name: string, value: string, description: string, enqueueSnackbar: EnqueueSnackbarType): void
}

const EditConfig: FC<IProps> = ({ config, editConfig, ...props }) => {

    const idRef = useRef(config.name)

    const exceptionConfigs = ['boxBottomMaterialPrice']
    const [configProps, setConfigProps] = useState({ value: config.value, description: config.description })

    const configValueRef = useRef(configProps)

    const [needSave, setNeedSave] = useState(false)
    const needSaveRef = useRef(needSave)

    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        idRef.current = config.name
        setConfigProps({ value: config.value, description: config.description })

        setNeedSave(false)
    }, [config])

    useEffect(() => {
        configValueRef.current = configProps
        needSaveRef.current = needSave
    }, [needSave, configProps])

    useEffect(() => () => {
        if (needSaveRef.current) {
            if (window?.confirm('Save config?')) {
                handleSaveClick()
            }
        }
    }, [config])

    const handleSaveClick = () => {
        const value = configValueRef.current.value

        if (value === '') {
            showMessage(enqueueSnackbar, "Enter value")
            return
        }

        needSaveRef.current = false
        setNeedSave(false)

        editConfig(idRef.current, value, configValueRef.current.description, enqueueSnackbar)
    }

    return (
        <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            pb={2}
            style={{
                width: 400,
                height: 'fit-content',
                margin: 15,
                border: '1px solid black',
                maxHeight: '90%',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
            }}
        >

            <Box
                py={1}
                style={{
                    position: 'sticky',
                    top: 0,
                    width: '100%',
                    backgroundColor: 'white',
                    zIndex: 10
                }}>
                <Grid container spacing={2}>
                    <Grid item xs={3}> </Grid>
                    <Grid item xs={6} container justifyContent='center' alignItems='center'>
                        <Typography variant='h6'>Edit Config</Typography>
                    </Grid>
                    <Grid item xs={3} container justifyContent='flex-end' alignItems='center'>
                        <Tooltip title={'Save'}>
                            <Fab
                                color={needSave ? 'secondary' : "primary"}
                                size='small'
                                onClick={handleSaveClick}
                                style={{ marginRight: 10 }}
                            >
                                <SaveIcon />
                            </Fab>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Box>

            <TextField
                label="Name"
                variant="outlined"
                value={idRef.current}
                disabled
                style={{ width: '60%', marginTop: 15 }}
            />

            <TextField
                label="Description"
                variant="outlined"
                value={configProps.description}
                onChange={e => {
                    setConfigProps({ ...configProps, description: e.target.value.replace(/['"]/, '') })
                    setNeedSave(true)
                }}
                style={{ width: '60%', marginTop: 15 }}
            />

            <hr style={{ margin: '15px', height: '1px', border: 0, borderTop: '1px solid hsl(0,0%,80%)', width: '100%' }} />


            {(JSON.parse(configProps.value) !== null && typeof JSON.parse(configProps.value) === 'object')
                ?
                Object.keys(JSON.parse(configProps.value)).map(key => {
                    if ((JSON.parse(configProps.value)[key] !== null && typeof JSON.parse(configProps.value)[key] === 'object') || exceptionConfigs.includes(key)) {
                        return <></>
                    }
                    else {
                        if (key === 'array') //hinge breakpoints
                            return <TextField
                                key={key}
                                label={key}
                                variant="outlined"
                                multiline
                                value={JSON.parse(configProps.value)[key]}
                                onChange={(e) => {
                                    const newValue = e.target.value

                                    const pattern = /^[0-9|\.|\,| ]*$/
                                    if (!pattern.test(newValue)) {
                                        showMessage(enqueueSnackbar, 'only number and commas are allowed')
                                        return
                                    }
                                    const config = JSON.parse(configProps.value)
                                    config[key] = newValue
                                    setConfigProps({ ...configProps, value: JSON.stringify(config) })
                                    setNeedSave(true)

                                }}
                                style={{ width: '60%', marginTop: 15 }}
                            />
                        else if (key === 'strValues') {
                            return <TextField
                                key={key}
                                label={key}
                                variant="outlined"
                                multiline
                                value={JSON.parse(configProps.value)[key]}
                                onChange={(e) => {
                                    const newValue = e.target.value

                                    const config = JSON.parse(configProps.value)
                                    config[key] = newValue
                                    setConfigProps({ ...configProps, value: JSON.stringify(config) })
                                    setNeedSave(true)

                                }}
                                style={{ width: '60%', marginTop: 15 }}
                            />
                        }
                        else return <TextField
                            key={key}
                            label={key}
                            variant="outlined"
                            type='number'
                            value={JSON.parse(configProps.value)[key]}
                            onChange={(e) => {
                                let newValue = e.target.value

                                if (newValue !== '') {
                                    if (parseFloat(newValue) < 0) {
                                        newValue = '0'
                                    }
                                }
                                else {
                                    newValue = '0'
                                }

                                const config = JSON.parse(configProps.value)
                                config[key] = parseFloat(newValue)
                                setConfigProps({ ...configProps, value: JSON.stringify(config) })

                                setNeedSave(true)
                            }}
                            style={{ width: '60%', marginTop: 15 }}
                        />
                    }
                })

                : <TextField
                    label="Value"
                    variant="outlined"
                    type='number'
                    value={configProps.value}
                    onChange={(e) => {
                        let newValue = e.target.value

                        if (newValue !== '') {
                            if (parseFloat(newValue) < 0) {
                                newValue = '0'
                            }
                        }
                        else {
                            newValue = '0'
                        }

                        setConfigProps({ ...configProps, value: newValue })
                        setNeedSave(true)
                    }}
                    style={{ width: '60%', marginTop: 15 }}
                />
            }
        </Box >
    )
}

export default EditConfig